import type { DayTone, HeatTier } from '../../shared/travel_calendar.js';
import type { PlannerHeatView } from './heat_view.js';

/*
 * O que o HEAT diz de cada dia da grade do planner, num módulo puro: os tiers
 * por dia de cada perna da forma binária, o estado de um dia (escolha, calor
 * ou nada) e a tradução para a camada de cor do `TravelCalendar` (`DayTone`).
 * Nasceu em `month_grid.tsx`, quando a grade era do planner; a grade hoje é o
 * calendário compartilhado, e o que ficou do planner é só saber o preço.
 */

/**
 * O que o heat da forma BINÁRIA diz sobre a grade: quantas datas analisou,
 * se há tendência, e os tiers por dia de cada perna — só quando ele descreve
 * a rota viva do formulário. Mora junto de `tierDayState`, que consome os tiers.
 */
export function binaryHeatMeta(
  heat: PlannerHeatView,
  oneWayTab: boolean,
  routeOk: boolean,
  loading: boolean,
  route: { origin: string; destination: string },
) {
  const describesRoute =
    heat.route.origin === route.origin && heat.route.destination === route.destination;
  const analyzed = describesRoute
    ? (heat.outbound?.days.length ?? 0) + (oneWayTab ? 0 : (heat.return?.days.length ?? 0))
    : 0;
  const noHeat = routeOk && !loading && analyzed === 0;
  const showCalMeta = loading || (routeOk && !noHeat);
  const days = (side: 'outbound' | 'return') => (describesRoute ? (heat[side]?.days ?? []) : []);
  const outTiers = new Map(days('outbound').map((day): [string, number] => [day.date, day.tier]));
  const retTiers = new Map(days('return').map((day): [string, number] => [day.date, day.tier]));
  // Só shimmera onde não há NADA a mostrar: com tiers em cena a grade os mantém
  // pintados durante a leitura da janela seguinte.
  const pricesLoading = loading && analyzed === 0;
  return { analyzed, noHeat, showCalMeta, outTiers, retTiers, pricesLoading };
}

type DayState =
  | { kind: 'pick'; tag: 'ida' | 'volta' }
  | { kind: 'range' }
  | { kind: 'heat'; tier: number }
  | { kind: 'nodata' };

/** A escolha de uma data no par ida/volta — pontas ou meio. */
function selectionState(date: string, out?: string, ret?: string): DayState | undefined {
  if (out && date === out) return { kind: 'pick', tag: 'ida' };
  if (ret && date === ret) return { kind: 'pick', tag: 'volta' };
  if (out && ret && date > out && date < ret) return { kind: 'range' };
  return undefined;
}

/**
 * O estado de um dia da forma BINÁRIA: a escolha vence; antes da ida, na fase
 * da volta, não há volta possível (célula sem cor, além de bloqueada pelo
 * piso); depois da ida a cor é a da VOLTA quando o heat a trouxe, senão a da
 * ida. Recebe os tiers para servir tanto à janela corrente (popover) quanto ao
 * arquivo de janelas da folha (`sheet_months`).
 */
export function tierDayState(
  date: string,
  {
    out,
    ret,
    pickingReturn,
    outTiers,
    retTiers,
  }: {
    out: string | undefined;
    ret: string | undefined;
    pickingReturn: boolean;
    outTiers: ReadonlyMap<string, number>;
    retTiers: ReadonlyMap<string, number>;
  },
): DayState {
  const selected = selectionState(date, out, ret);
  if (selected) return selected;
  if (pickingReturn && out && date < out) return { kind: 'nodata' };
  const tier = out && date > out ? (retTiers.get(date) ?? outTiers.get(date)) : outTiers.get(date);
  return tier ? { kind: 'heat', tier } : { kind: 'nodata' };
}

function heatTier(tier: number): HeatTier | undefined {
  return tier === 1 || tier === 2 || tier === 3 || tier === 4 || tier === 5 ? tier : undefined;
}

/**
 * A camada de cor de um dia para o `TravelCalendar`: o tier pintado; a espera
 * (`loading`) SÓ onde não há tier a mostrar — os dois na mesma célula seriam a
 * cor piscando; e nada na escolha, que o calendário desenha em ink.
 *
 * Escolher data NUNCA depende do preço (#2330): a espera mora na camada de
 * cor, e o dia segue botão.
 */
export function dayToneOf(state: DayState, loading: boolean): DayTone | undefined {
  if (state.kind === 'heat') {
    const tier = heatTier(state.tier);
    if (tier) return { kind: 'heat', tier };
  }
  if (state.kind === 'nodata' && loading) return { kind: 'loading' };
  return undefined;
}

/** A camada de cor a partir de um mapa de tiers (o multi: um por perna). */
export function tiersTone(
  tiers: ReadonlyMap<string, number>,
  loading: boolean,
): (date: string) => DayTone | undefined {
  return (date) => {
    const tier = tiers.get(date);
    return dayToneOf(tier ? { kind: 'heat', tier } : { kind: 'nodata' }, loading);
  };
}
